import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiCheck, FiX } from "react-icons/fi";
import PageName from "../../components/PageName";
import Card from "../../components/Base/Card";
import ErrorViewer from "../../components/ErrorViewer";
import LoadingSpinner from "../../components/LoadingSpinner";
import SuccessModal from "../../components/SuccessModal";
import EmployerSelect from "../../components/EmployerSelect";
import SearchableSelect from "../../components/SearchableSelect";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getCountries, getLGA, getStates } from "../../redux/utils/utilsSlice";
import {
  GetAllEmployerBranch,
  GetRelationship,
} from "../../redux/selfService/selfServiceSlice";
import selfServicesService from "../../redux/selfService/selfServicesService";
import utilsService from "../../redux/utils/utilsService";
import { RouteNames } from "../../constants";
import { EmployerSelectValue } from "../../interfaces";
import InactivityTimeoutHOC from "../auth/inactivity-timeout-hoc";

const TABS = [
  { id: 0, title: "Personal Data" },
  { id: 1, title: "Employer" },
  { id: 2, title: "Monthly Contribution" },
  { id: 3, title: "Next Of Kin" },
  { id: 4, title: "Documents" },
  { id: 5, title: "Review" },
];

const TITLE_OPTIONS = [
  { value: 1, label: "Miss" },
  { value: 2, label: "Mr" },
  { value: 3, label: "Mrs" },
  { value: 4, label: "Ms" },
];
const GENDER_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];
const NOK_GENDER_OPTIONS = [
  { value: 1, label: "Male" },
  { value: 2, label: "Female" },
];
const MARITAL_OPTIONS = [
  { value: 1, label: "Separated" },
  { value: 2, label: "Widow" },
  { value: 3, label: "Divorce" },
  { value: 4, label: "Married" },
  { value: 5, label: "Single" },
];
const NATIONALITY_OPTIONS = [
  { value: "1", label: "Nigerian" },
  { value: "0", label: "Non-Nigerian" },
];
const REQUEST_TYPE_OPTIONS = [
  { value: 0, label: "New RSA" },
  { value: 1, label: "Temporary PIN" },
];
const USER_TYPE_OPTIONS = [
  { value: "ADULT", label: "Adult" },
  { value: "MINOR", label: "Minor" },
];
const CATEGORY_OPTIONS = [
  { value: 0, label: "Formal Sector" },
  { value: 2, label: "Personal Plan" },
  { value: 3, label: "Cross Border" },
  { value: 4, label: "Non-Interest" },
];

const TAB_FIELDS: string[][] = [
  ["requestType", "rsaUserType", "category", "title", "gender", "surname", "firstName", "maritalStatusId", "bvn", "nin", "dateOfBirth", "placeOfBirth", "phoneCountryCode", "phoneNumber", "nationality", "originStateCode", "lgaOfOriginCodeId", "residenceCountryCode", "residenceStateCode", "residenceLocalGovernmentId", "residenceTownCity", "residenceStreetName", "residenceHouseNameOrNumber"],
  ["employerBranchId"],
  ["monthlyEmolument", "expectedEmployeeContribution", "expectedEmployerContribution"],
  ["nokTitle", "nokGender", "nokSurname", "nokFirstName", "relationshipId", "nokResidenceCountryId", "nokPhoneNumber"],
  [],
  ["acknowledged"],
];

const validationSchema = Yup.object().shape({
  requestType: Yup.number().required("Request type is required"),
  rsaUserType: Yup.string().required("User type is required"),
  category: Yup.number().required("Category is required"),
  bvn: Yup.string().required("BVN is required").max(15),
  nin: Yup.string().required("NIN is required").min(11).max(15),
  title: Yup.number().required("Title is required"),
  gender: Yup.string().required("Gender is required"),
  surname: Yup.string().required("Surname is required"),
  firstName: Yup.string().required("First name is required"),
  middleName: Yup.string(),
  maidenOrFormerName: Yup.string(),
  maritalStatusId: Yup.number().required("Marital status is required"),
  nationality: Yup.string().required("Nationality is required"),
  originStateCode: Yup.string().required("State of origin is required"),
  lgaOfOriginCodeId: Yup.number().required("LGA of origin is required"),
  dateOfBirth: Yup.string().required("Date of birth is required"),
  placeOfBirth: Yup.string().required("Place of birth is required"),
  email: Yup.string().transform((v) => (!v ? null : v)).email("Invalid email").nullable(),
  phoneCountryCode: Yup.string().required("Phone prefix is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  residenceCountryCode: Yup.string().required("Residence country is required"),
  residenceStateCode: Yup.string().required("Residence state is required"),
  residenceLocalGovernmentId: Yup.number().required("LGA is required"),
  residenceTownCity: Yup.string().required("Town/City is required"),
  residenceStreetName: Yup.string().required("Street is required"),
  residenceHouseNameOrNumber: Yup.string().required("House number is required"),
  residenceZipCode: Yup.string().nullable(),
  employerBranchId: Yup.number().required("Employer branch is required"),
  natureOfBusiness: Yup.string().nullable(),
  officePhone: Yup.string().nullable(),
  officeEmail: Yup.string().transform((v) => (!v ? null : v)).email("Invalid office email").nullable(),
  jobDesignation: Yup.string().nullable(),
  grade: Yup.string().nullable(),
  employeeNumber: Yup.string().nullable(),
  dateOfFirstAppointment: Yup.string().nullable(),
  dateOfCurrentEmployment: Yup.string().nullable(),
  employmentStateCode: Yup.string().nullable(),
  employmentTownCity: Yup.string().nullable(),
  employmentStreetName: Yup.string().nullable(),
  employmentBuildingNameOrNumber: Yup.string().nullable(),
  employmentLocalGovernmentId: Yup.number().nullable(),
  monthlyEmolument: Yup.number().nullable(),
  expectedEmployerContribution: Yup.number().nullable(),
  expectedEmployeeContribution: Yup.number().nullable(),
  annualSalary: Yup.number().nullable(),
  avc: Yup.number().nullable(),
  nokTitle: Yup.number().required("NOK title is required"),
  nokGender: Yup.number().required("NOK gender is required"),
  nokSurname: Yup.string().required("NOK surname is required"),
  nokFirstName: Yup.string().required("NOK first name is required"),
  nokMiddleName: Yup.string().nullable(),
  relationshipId: Yup.number().required("Relationship is required"),
  nokResidenceCountryId: Yup.number().required("NOK country is required"),
  nokPhoneNumber: Yup.string().required("NOK phone is required"),
  nokEmail: Yup.string().transform((v) => (!v ? null : v)).email("Invalid email").nullable(),
  nokResidenceStreetName: Yup.string().nullable(),
  nokResidenceHouseNumber: Yup.string().nullable(),
  nokResidenceTownCity: Yup.string().nullable(),
  nokResidenceStateCode: Yup.string().nullable(),
  acknowledged: Yup.boolean().oneOf([true], "Please confirm that the information provided is accurate."),
});

const last10Digits = (value?: string | null) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length <= 10 ? digits : digits.slice(-10);
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const toDateInput = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const normalizeNationality = (value?: string | null) => {
  if (value == null || value === "") return "1";
  const v = String(value).trim();
  if (v === "1" || v.toUpperCase() === "NG" || v.toLowerCase() === "nigerian" || v.toUpperCase() === "N") return "1";
  return "0";
};

const flagEmoji = (iso?: string) => {
  if (!iso || iso.length !== 2) return "";
  return String.fromCodePoint(...iso.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)));
};

const previewSrc = (src?: string | null) => {
  if (!src) return null;
  if (src.startsWith("data:") || src.startsWith("blob:") || src.startsWith("http")) return src;
  return `data:image/jpeg;base64,${src}`;
};

const optionLabel = (options: { value: any; label: string }[], value: any) =>
  options.find((o) => String(o.value) === String(value))?.label || (value != null && value !== "" ? String(value) : "");

const Field = ({ label, children, error }: { label: string; children: React.ReactNode; error?: any }) => (
  <div className="form__group">
    <label className="form__label">{label}</label>
    {children}
    {error && <ErrorViewer message={error} />}
  </div>
);

const ReviewRow = ({ label, value }: { label: string; value?: any }) => {
  const filled = value !== undefined && value !== null && String(value).trim() !== "";
  return (
    <div className="flex items-stretch border-b border-slate-200 bg-white min-h-[52px]">
      <div className="w-[38%] px-4 py-3 text-sm text-slate-600 self-center">{label}</div>
      <div className="flex-1 px-4 py-3 text-sm font-medium text-slate-800 self-center break-words">{filled ? String(value) : ""}</div>
      <div className={`w-12 flex items-center justify-center text-white ${filled ? "bg-emerald-500" : "bg-orange-500"}`}>
        {filled ? <FiCheck size={18} /> : <FiX size={18} />}
      </div>
    </div>
  );
};

const CompleteRegistrationPage = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { allStates, lgas, allCountries } = useAppSelector((state) => state.utils);
  const { relationships, allEmployerBranch, isLoading: branchLoading } = useAppSelector((state) => state.selfService);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusDisplay, setStatusDisplay] = useState("");
  const [picturePreview, setPicturePreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [consentPreview, setConsentPreview] = useState<string | null>(null);
  const [consentIsPdf, setConsentIsPdf] = useState(false);
  const [employerDefault, setEmployerDefault] = useState<EmployerSelectValue | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const [hasExistingPicture, setHasExistingPicture] = useState(false);
  const [hasExistingSignature, setHasExistingSignature] = useState(false);
  const [hasExistingConsent, setHasExistingConsent] = useState(false);
  const [faceValidating, setFaceValidating] = useState(false);
  const [faceCompliant, setFaceCompliant] = useState<boolean | null>(null);
  const [faceStatusMessage, setFaceStatusMessage] = useState("");
  const [originLgas, setOriginLgas] = useState<Record<string, string>>({});
  const [pictureName, setPictureName] = useState("");
  const [signatureName, setSignatureName] = useState("");
  const [consentName, setConsentName] = useState("");
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [rsaPin, setRsaPin] = useState("");

  const { register, handleSubmit, reset, setValue, getValues, watch, trigger, control, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      nationality: "1",
      residenceCountryCode: "NG",
      gender: "M",
      requestType: 0,
      rsaUserType: "ADULT",
      category: 0,
      phoneCountryCode: "NG",
      acknowledged: false,
    },
  });

  const residenceStateCode = watch("residenceStateCode");
  const originStateCode = watch("originStateCode");
  const employerBranchId = watch("employerBranchId");
  const formValues = watch();

  useEffect(() => {
    if (!user?.isRegistrationIncomplete) return;
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([dispatch(getStates()), dispatch(getCountries()), dispatch(GetRelationship())]);
        const response = await selfServicesService.getRegistrationDetails();
        const data = response?.data?.responseObject;
        if (!data) {
          toast.error(response?.data?.statusMessage || "Unable to load registration details.");
          return;
        }
        setCanEdit(!!(data.canEdit ?? data.canSubmit ?? true) && !data.isSubmitted);
        setIsSubmitted(!!data.isSubmitted);
        setStatusMessage(data.statusMessage || "");
        setStatusDisplay(data.statusDisplay || "");
        setLastUpdated(data.submittedDate || data.lastUpdated || null);
        setRsaPin(data.pin || user?.pin || "");
        setPicturePreview(data.picturePreview || null);
        setHasExistingPicture(!!data.hasPicture);
        setHasExistingSignature(!!data.hasSignature);
        setHasExistingConsent(!!data.hasConsentForm);
        if (data.hasPicture) setPictureName("Passport photograph on file");
        if (data.signaturePreview) {
          setSignaturePreview(data.signaturePreview.startsWith("data:") ? data.signaturePreview : `data:image/jpeg;base64,${data.signaturePreview}`);
          setSignatureName("Signature on file");
        }
        if (data.consentPreview) {
          const isPdf = !!data.consentIsPdf;
          setConsentIsPdf(isPdf);
          setConsentPreview(data.consentPreview.startsWith("data:") ? data.consentPreview : isPdf ? `data:application/pdf;base64,${data.consentPreview}` : `data:image/jpeg;base64,${data.consentPreview}`);
          setConsentName(isPdf ? "Consent form (PDF) on file" : "Consent form on file");
        }
        const p = data.personalData || {};
        const e = data.employmentRecord || {};
        const n = data.nextOfKinDetail || {};
        const m = data.monthlyContribution || {};
        if (p.residenceStateCode) await dispatch(getLGA({ stateCode: p.residenceStateCode }));
        if (p.stateOfOriginCode) {
          const originRes = await utilsService.getLGA({ stateCode: p.stateOfOriginCode });
          setOriginLgas(originRes?.data?.responseObject?.lgAs || {});
        }
        if (e.employerId) {
          await dispatch(GetAllEmployerBranch({ employerId: e.employerId }));
          if (e.employerName) {
            setEmployerDefault({ label: e.employerName, value: { id: e.employerId, txtemployername: e.employerName } as any });
          }
        }
        reset({
          requestType: p.requestType ?? 0,
          rsaUserType: p.userType || "ADULT",
          category: p.category ?? 0,
          bvn: p.bvn || "",
          nin: p.nin || "",
          title: p.title || undefined,
          gender: p.gender || "M",
          surname: p.surname || user?.surname || "",
          firstName: p.firstName || user?.firstName || "",
          middleName: p.middleName || "",
          maidenOrFormerName: p.maidenOrFormerName || "",
          maritalStatusId: p.maritalStatusId || undefined,
          nationality: normalizeNationality(p.nationality),
          originStateCode: p.stateOfOriginCode || "",
          lgaOfOriginCodeId: p.lgaOfOriginCodeId || undefined,
          dateOfBirth: toDateInput(p.dateOfBirth),
          placeOfBirth: p.placeOfBirth || "",
          email: p.email || "",
          phoneCountryCode: "NG",
          phoneNumber: last10Digits(p.internationalNumber || p.phoneNumber),
          residenceCountryCode: p.residenceCountryCode || "NG",
          residenceStateCode: p.residenceStateCode || "",
          residenceLocalGovernmentId: p.residenceLocalGovernmentId || undefined,
          residenceTownCity: p.residenceTownCity || "",
          residenceStreetName: p.residenceStreetName || "",
          residenceHouseNameOrNumber: p.residenceHouseNameOrNumber || "",
          residenceZipCode: p.residenceZipCode || "",
          employerBranchId: e.employerBranchId || undefined,
          natureOfBusiness: e.natureOfBusiness || "",
          officePhone: e.officePhone || "",
          officeEmail: e.officeEmail || "",
          jobDesignation: e.jobDesignation || "",
          grade: e.grade || "",
          employeeNumber: e.employeeNumber || "",
          dateOfFirstAppointment: toDateInput(e.dateOfFirstAppointment),
          dateOfCurrentEmployment: toDateInput(e.dateOfCurrentEmployment),
          employmentStateCode: e.stateCode || "",
          employmentTownCity: e.townCity || "",
          employmentStreetName: e.streetName || "",
          employmentBuildingNameOrNumber: e.buildingNameOrNumber || "",
          employmentLocalGovernmentId: e.localGovernmentId || undefined,
          monthlyEmolument: m.monthlyEmolument ?? undefined,
          expectedEmployerContribution: m.expectedEmployerContribution ?? undefined,
          expectedEmployeeContribution: m.expectedEmployeeContribution ?? undefined,
          annualSalary: m.annualSalary ?? undefined,
          avc: m.avc ?? undefined,
          nokTitle: n.title || undefined,
          nokGender: n.gender || undefined,
          nokSurname: n.surname || "",
          nokFirstName: n.firstName || "",
          nokMiddleName: n.middleName || "",
          relationshipId: n.relationshipId || undefined,
          nokResidenceCountryId: n.residenceCountryId || undefined,
          nokPhoneNumber: n.phoneNumber || "",
          nokEmail: n.email || "",
          nokResidenceStreetName: n.residenceStreetName || "",
          nokResidenceHouseNumber: n.residenceHouseNumber || "",
          nokResidenceTownCity: n.residenceTownCity || "",
          nokResidenceStateCode: n.residenceStateCode || "",
          acknowledged: false,
        });
        setHasAcknowledged(false);
      } catch (err: any) {
        toast.error(err?.response?.data?.statusMessage || "Unable to load registration details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dispatch, reset, user?.isRegistrationIncomplete, user?.firstName, user?.surname]);

  useEffect(() => {
    if (residenceStateCode) dispatch(getLGA({ stateCode: residenceStateCode }));
  }, [dispatch, residenceStateCode]);

  useEffect(() => {
    if (!originStateCode) {
      setOriginLgas({});
      return;
    }
    let cancelled = false;
    utilsService.getLGA({ stateCode: originStateCode }).then((res) => {
      if (!cancelled) setOriginLgas(res?.data?.responseObject?.lgAs || {});
    }).catch(() => {
      if (!cancelled) setOriginLgas({});
    });
    return () => { cancelled = true; };
  }, [originStateCode]);

  const branchOptions = useMemo(
    () => (allEmployerBranch || []).map((b: any) => ({
      value: b.id,
      label: b.txtEmployerBranchName || b.txtemployerbranchname || b.name || `Branch ${b.id}`,
    })),
    [allEmployerBranch]
  );

  const countryCodeOptions = useMemo(
    () => (allCountries || []).map((c: any) => ({
      value: c.txtcountrycode || c.txtCountryCode,
      label: c.txtcountryName || c.txtCountryName || c.txtcountrycode || c.txtCountryCode,
    })).filter((o: any) => o.value),
    [allCountries]
  );

  const phonePrefixOptions = useMemo(() => {
    const opts = (allCountries || []).map((c: any) => {
      const code = c.txtcountrycode || c.txtCountryCode;
      const dial = String(c.phoneCode || "").replace(/^\+/, "");
      return { value: code, label: `${flagEmoji(code)} +${dial || ""}`.trim() };
    }).filter((o: any) => o.value);
    return opts.sort((a: any, b: any) => (a.value === "NG" ? -1 : b.value === "NG" ? 1 : a.label.localeCompare(b.label)));
  }, [allCountries]);

  const countryIdOptions = useMemo(
    () => (allCountries || []).map((c: any) => ({
      value: c.id,
      label: c.txtcountryName || c.txtCountryName || c.txtcountrycode,
    })).filter((o: any) => o.value != null),
    [allCountries]
  );

  const stateOptions = useMemo(
    () => Object.keys(allStates || {}).map((code) => ({ value: code, label: allStates[code] })),
    [allStates]
  );

  const lgaOptions = useMemo(
    () => Object.keys(lgas || {}).map((id) => ({ value: Number(id), label: lgas[id] })),
    [lgas]
  );

  const originLgaOptions = useMemo(
    () => Object.keys(originLgas || {}).map((id) => ({ value: Number(id), label: originLgas[id] })),
    [originLgas]
  );

  const relationshipOptions = useMemo(
    () => (relationships || []).map((r: any) => ({
      value: r.id,
      label: r.txtRelationshipType || r.txtrelationship || r.name || `Relationship ${r.id}`,
    })),
    [relationships]
  );

  const onEmployerSelected = (selected: EmployerSelectValue | null) => {
    setEmployerDefault(selected);
    const employerId = (selected?.value as any)?.id;
    setValue("employerBranchId", undefined as any, { shouldValidate: true });
    if (employerId) dispatch(GetAllEmployerBranch({ employerId }));
  };

  const revokeUrl = (url: string | null) => {
    if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const onPictureSelected = async (file: File | null) => {
    revokeUrl(picturePreview?.startsWith("blob:") ? picturePreview : null);
    setPictureFile(file);
    setFaceCompliant(null);
    setFaceStatusMessage("");
    setPictureName(file?.name || "");
    if (!file) { setPicturePreview(null); return; }
    setPicturePreview(URL.createObjectURL(file));
    setFaceValidating(true);
    try {
      const response = await selfServicesService.validateRecapturePassport(file);
      const compliant = response?.data?.responseObject === true;
      const message = response?.data?.statusMessage || (compliant ? "Face is Compliant as Passport Passed all checks" : "Face is Non-Compliant, please retake passport.");
      setFaceCompliant(compliant);
      setFaceStatusMessage(message);
      compliant ? toast.success(message) : toast.error(message);
    } catch (err: any) {
      const message = err?.response?.data?.statusMessage || "Unable to validate passport photograph.";
      setFaceCompliant(false);
      setFaceStatusMessage(message);
      toast.error(message);
    } finally {
      setFaceValidating(false);
    }
  };

  const onSignatureSelected = (file: File | null) => {
    revokeUrl(signaturePreview?.startsWith("blob:") ? signaturePreview : null);
    setSignatureFile(file);
    setSignatureName(file?.name || "");
    setSignaturePreview(file ? URL.createObjectURL(file) : null);
  };

  const onConsentSelected = (file: File | null) => {
    revokeUrl(consentPreview?.startsWith("blob:") ? consentPreview : null);
    setConsentFile(file);
    setConsentName(file?.name || "");
    if (!file) { setConsentPreview(null); setConsentIsPdf(false); return; }
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    setConsentIsPdf(isPdf);
    setConsentPreview(URL.createObjectURL(file));
  };

  const append = (form: FormData, key: string, value: any) => {
    if (value === undefined || value === null || value === "") return;
    form.append(key, String(value));
  };

  const buildInternationalNumber = (values: any) => {
    const country = (allCountries || []).find((c: any) => (c.txtcountrycode || c.txtCountryCode) === (values.phoneCountryCode || "NG"));
    const dial = String(country?.phoneCode || "234").replace(/^\+/, "") || "234";
    const local = last10Digits(values.phoneNumber);
    return local ? `+${dial}${local}` : "";
  };

  const buildFormData = (values: any, includeDocs: boolean) => {
    const form = new FormData();
    append(form, "PersonalData.Bvn", values.bvn);
    append(form, "PersonalData.Nin", values.nin);
    append(form, "PersonalData.Title", values.title);
    append(form, "PersonalData.Gender", values.gender);
    append(form, "PersonalData.Surname", values.surname);
    append(form, "PersonalData.FirstName", values.firstName);
    append(form, "PersonalData.MiddleName", values.middleName);
    append(form, "PersonalData.MaidenOrFormerName", values.maidenOrFormerName);
    append(form, "PersonalData.MaritalStatusId", values.maritalStatusId);
    append(form, "PersonalData.Nationality", normalizeNationality(values.nationality));
    append(form, "PersonalData.RequestType", values.requestType);
    append(form, "PersonalData.UserType", values.rsaUserType);
    append(form, "PersonalData.Category", values.category);
    append(form, "PersonalData.StateOfOriginCode", values.originStateCode);
    append(form, "PersonalData.LGAOfOriginCodeId", values.lgaOfOriginCodeId);
    append(form, "PersonalData.DateOfBirth", values.dateOfBirth);
    append(form, "PersonalData.PlaceOfBirth", values.placeOfBirth);
    append(form, "PersonalData.Email", values.email);
    append(form, "PersonalData.PhoneCountryCode", values.phoneCountryCode || "NG");
    append(form, "PersonalData.PhoneNumber", last10Digits(values.phoneNumber));
    append(form, "PersonalData.InternationalNumber", buildInternationalNumber(values));
    append(form, "PersonalData.ResidenceCountryCode", values.residenceCountryCode);
    append(form, "PersonalData.ResidenceStateCode", values.residenceStateCode);
    append(form, "PersonalData.ResidenceLocalGovernmentId", values.residenceLocalGovernmentId);
    append(form, "PersonalData.ResidenceTownCity", values.residenceTownCity);
    append(form, "PersonalData.ResidenceStreetName", values.residenceStreetName);
    append(form, "PersonalData.ResidenceHouseNameOrNumber", values.residenceHouseNameOrNumber);
    append(form, "PersonalData.ResidenceZipCode", values.residenceZipCode);
    if (values.employerBranchId) append(form, "EmploymentRecord.EmployerBranchId", values.employerBranchId);
    append(form, "EmploymentRecord.StateCode", values.employmentStateCode);
    append(form, "EmploymentRecord.LocalGovernmentId", values.employmentLocalGovernmentId);
    append(form, "EmploymentRecord.TownCity", values.employmentTownCity);
    append(form, "EmploymentRecord.StreetName", values.employmentStreetName);
    append(form, "EmploymentRecord.BuildingNameOrNumber", values.employmentBuildingNameOrNumber);
    append(form, "EmploymentRecord.NatureOfBusiness", values.natureOfBusiness);
    append(form, "EmploymentRecord.OfficePhone", values.officePhone);
    append(form, "EmploymentRecord.OfficeEmail", values.officeEmail);
    append(form, "EmploymentRecord.JobDesignation", values.jobDesignation);
    append(form, "EmploymentRecord.Grade", values.grade);
    append(form, "EmploymentRecord.EmployeeNumber", values.employeeNumber);
    append(form, "EmploymentRecord.DateOfFirstAppointment", values.dateOfFirstAppointment);
    append(form, "EmploymentRecord.DateOfCurrentEmployment", values.dateOfCurrentEmployment);
    append(form, "MonthlyContribution.MonthlyEmolument", values.monthlyEmolument);
    append(form, "MonthlyContribution.ExpectedEmployerContribution", values.expectedEmployerContribution);
    append(form, "MonthlyContribution.ExpectedEmployeeContribution", values.expectedEmployeeContribution);
    append(form, "MonthlyContribution.AnnualSalary", values.annualSalary);
    append(form, "MonthlyContribution.Avc", values.avc);
    append(form, "NextOfKinDetail.Title", values.nokTitle);
    append(form, "NextOfKinDetail.Gender", values.nokGender);
    append(form, "NextOfKinDetail.Surname", values.nokSurname);
    append(form, "NextOfKinDetail.FirstName", values.nokFirstName);
    append(form, "NextOfKinDetail.MiddleName", values.nokMiddleName);
    append(form, "NextOfKinDetail.RelationshipId", values.relationshipId);
    append(form, "NextOfKinDetail.ResidenceCountryId", values.nokResidenceCountryId);
    append(form, "NextOfKinDetail.ResidenceStateCode", values.nokResidenceStateCode);
    append(form, "NextOfKinDetail.ResidenceTownCity", values.nokResidenceTownCity);
    append(form, "NextOfKinDetail.ResidenceStreetName", values.nokResidenceStreetName);
    append(form, "NextOfKinDetail.ResidenceHouseNumber", values.nokResidenceHouseNumber);
    append(form, "NextOfKinDetail.Email", values.nokEmail);
    append(form, "NextOfKinDetail.PhoneNumber", values.nokPhoneNumber);
    if (includeDocs) {
      let docIndex = 0;
      if (pictureFile) {
        form.append(`RegistrationDocuments[${docIndex}].DocumentType`, "1");
        form.append(`RegistrationDocuments[${docIndex}].File`, pictureFile);
        docIndex += 1;
      }
      if (signatureFile) {
        form.append(`RegistrationDocuments[${docIndex}].DocumentType`, "2");
        form.append(`RegistrationDocuments[${docIndex}].File`, signatureFile);
        docIndex += 1;
      }
      if (consentFile) {
        form.append(`RegistrationDocuments[${docIndex}].DocumentType`, "3");
        form.append(`RegistrationDocuments[${docIndex}].File`, consentFile);
      }
    }
    return form;
  };

  const goNext = async () => {
    const fields = TAB_FIELDS[tab];
    if (fields.length) {
      const ok = await trigger(fields as any);
      if (!ok) {
        toast.error("Please complete the required fields on this tab.");
        return;
      }
    }
    if (tab === 4 && !pictureFile && !hasExistingPicture) {
      toast.error("Please upload your passport photograph.");
      return;
    }
    if (tab === 4 && (faceValidating || faceCompliant === false || (pictureFile && faceCompliant !== true))) {
      toast.error(faceStatusMessage || "Passport must pass face validation before submit.");
      return;
    }
    if (tab === 4 && !signatureFile && !hasExistingSignature) {
      toast.error("Please upload your signature.");
      return;
    }
    if (tab === 4 && !consentFile && !hasExistingConsent) {
      toast.error("Please upload the consent form.");
      return;
    }
    setTab((t) => Math.min(t + 1, TABS.length - 1));
  };

  const onSaveDraft = async () => {
    if (!canEdit) return;
    const form = buildFormData(getValues(), true);
    form.append("SaveAsDraft", "true");
    setSavingDraft(true);
    try {
      const response = await selfServicesService.saveRegistrationDraft(form);
      if (pictureFile) setHasExistingPicture(true);
      if (signatureFile) setHasExistingSignature(true);
      if (consentFile) setHasExistingConsent(true);
      toast.success(response?.data?.statusMessage || "Draft saved.");
    } catch (err: any) {
      const data = err?.response?.data;
      const modelErrors = data?.errors
        ? Object.values(data.errors).flat().join(" ")
        : "";
      toast.error(data?.statusMessage || data?.title || modelErrors || "Unable to save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const onSubmit = async (values: any) => {
    if (!canEdit) return;
    if (!hasAcknowledged) {
      toast.error("Please confirm that the information provided is accurate.");
      setTab(5);
      return;
    }
    if (!pictureFile && !hasExistingPicture) { toast.error("Please upload your passport photograph."); setTab(4); return; }
    if (faceValidating || faceCompliant === false || (pictureFile && faceCompliant !== true)) {
      toast.error(faceStatusMessage || "Passport must pass face validation before submit.");
      setTab(4);
      return;
    }
    if (!signatureFile && !hasExistingSignature) { toast.error("Please upload your signature."); setTab(4); return; }
    if (!consentFile && !hasExistingConsent) { toast.error("Please upload the consent form."); setTab(4); return; }
    const form = buildFormData(values, true);
    form.append("SaveAsDraft", "false");
    setSubmitting(true);
    try {
      const response = await selfServicesService.submitRegistration(form);
      if (response?.data?.statusCode === "00" || response?.data?.successful) {
        setShowSuccess(true);
        setCanEdit(false);
        setIsSubmitted(true);
        setStatusDisplay("Awaiting approval");
        setStatusMessage(response.data.statusMessage || "Your registration has been submitted. NLPC PFA will continue processing your RSA.");
        setLastUpdated(new Date().toISOString());
      } else {
        toast.error(response?.data?.statusMessage || "Submission failed.");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.statusMessage || "Unable to submit registration.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user?.isRegistrationIncomplete) {
    return <Navigate to={RouteNames.HOME} replace />;
  }

  const photoSrc = previewSrc(picturePreview);
  const signSrc = previewSrc(signaturePreview);
  const consentSrc = previewSrc(consentPreview);
  const faceBlocksSubmit = faceValidating || faceCompliant === false || (!!pictureFile && faceCompliant !== true);

  return (
    <div className="transaction-page settings-page">
      <PageName pageName="RSA Registration" />
      <SuccessModal showModal={showSuccess} onClose={() => setShowSuccess(false)} note="Your registration has been submitted. NLPC PFA will continue processing your RSA." />
      <Card className="transaction-card">
        {loading ? (
          <>
            <div className="h1 text-grayscaleBody mb-4">Complete RSA Registration</div>
            <LoadingSpinner size="md" />
          </>
        ) : isSubmitted || !canEdit ? (
          <>
            <div className="h1 text-grayscaleBody border-b pb-4 mb-4">RSA registration status</div>
            <div className="space-y-6">
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4">
                <p className="text-sm text-slate-500 mb-1">Current status</p>
                <p className="text-xl font-semibold text-grayscaleHeader">{statusDisplay || "Awaiting approval"}</p>
                {statusMessage && (
                  <p className="text-sm text-grayscaleBody mt-2">{statusMessage}</p>
                )}
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">PIN</label>
                  <div className="form__input bg-slate-50">{rsaPin || "-"}</div>
                </div>
                <div className="form__group">
                  <label className="form__label">Submitted / last saved</label>
                  <div className="form__input bg-slate-50">{formatDate(lastUpdated)}</div>
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Photo on file</label>
                  <div className="form__input bg-slate-50">{hasExistingPicture ? "Yes" : "No"}</div>
                </div>
                <div className="form__group">
                  <label className="form__label">Signature on file</label>
                  <div className="form__input bg-slate-50">{hasExistingSignature ? "Yes" : "No"}</div>
                </div>
              </div>
              <div className="form__group">
                <label className="form__label">Consent form on file</label>
                <div className="form__input bg-slate-50">{hasExistingConsent ? "Yes" : "No"}</div>
              </div>
              {employerDefault?.label && (
                <div className="form__group">
                  <label className="form__label">Employer</label>
                  <div className="form__input bg-slate-50">{employerDefault.label}</div>
                </div>
              )}
              <p className="text-sm text-slate-500">
                Editing is locked while your application is under review.
              </p>
            </div>
          </>
        ) : (
          <>
        <div className="h1 text-grayscaleBody mb-4">Complete RSA Registration</div>
        {(statusMessage || statusDisplay) && (
          <div className="mb-4 text-sm text-grayscaleBody bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
            {statusDisplay && <strong className="block mb-1">{statusDisplay}</strong>}
            {statusMessage}
          </div>
        )}
        <div className="flex flex-wrap gap-2 border-b mb-6">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`px-3 py-2 text-sm rounded-t-lg ${tab === item.id ? "font-semibold border-b-2 border-primary text-primary" : "text-slate-500"}`}
              onClick={() => setTab(item.id)}
            >
              <span className="mr-1">{item.id + 1}.</span>
              {item.title}
            </button>
          ))}
        </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!hasAcknowledged) {
                if (tab === 5) toast.error("Please tick the confirmation box before submitting.");
                return;
              }
              handleSubmit(onSubmit)();
            }}
            className="space-y-6"
          >
            <div className={`space-y-6 min-w-0 overflow-visible ${!canEdit ? "pointer-events-none opacity-60" : ""}`}>
              {tab === 0 && (
                <section className="overflow-visible">
                  <div className="form__row">
                    <Field label="Request type *" error={errors.requestType?.message}>
                      <Controller name="requestType" control={control} render={({ field }) => (
                        <SearchableSelect options={REQUEST_TYPE_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search request type" isClearable={false} />
                      )} />
                    </Field>
                    <Field label="User type *" error={errors.rsaUserType?.message}>
                      <Controller name="rsaUserType" control={control} render={({ field }) => (
                        <SearchableSelect options={USER_TYPE_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search user type" isClearable={false} />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Category *" error={errors.category?.message}>
                      <Controller name="category" control={control} render={({ field }) => (
                        <SearchableSelect options={CATEGORY_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search category" isClearable={false} />
                      )} />
                    </Field>
                    <Field label="Title *" error={errors.title?.message}>
                      <Controller name="title" control={control} render={({ field }) => (
                        <SearchableSelect options={TITLE_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search title" />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Gender *" error={errors.gender?.message}>
                      <Controller name="gender" control={control} render={({ field }) => (
                        <SearchableSelect options={GENDER_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search gender" />
                      )} />
                    </Field>
                    <Field label="Marital status *" error={errors.maritalStatusId?.message}>
                      <Controller name="maritalStatusId" control={control} render={({ field }) => (
                        <SearchableSelect options={MARITAL_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search marital status" />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Surname *" error={errors.surname?.message}><input className="form__input" {...register("surname")} /></Field>
                    <Field label="First name *" error={errors.firstName?.message}><input className="form__input" {...register("firstName")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Middle name"><input className="form__input" {...register("middleName")} /></Field>
                    <Field label="Maiden / former name"><input className="form__input" {...register("maidenOrFormerName")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Nationality *" error={errors.nationality?.message}>
                      <Controller name="nationality" control={control} render={({ field }) => (
                        <SearchableSelect options={NATIONALITY_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search nationality" isClearable={false} />
                      )} />
                    </Field>
                    <Field label="BVN *" error={errors.bvn?.message}><input className="form__input" {...register("bvn")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="NIN *" error={errors.nin?.message}><input className="form__input" {...register("nin")} /></Field>
                    <Field label="Date of birth *" error={errors.dateOfBirth?.message}><input type="date" className="form__input" {...register("dateOfBirth")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Place of birth *" error={errors.placeOfBirth?.message}><input className="form__input" {...register("placeOfBirth")} /></Field>
                    <Field label="Email" error={errors.email?.message}><input className="form__input" type="email" {...register("email")} /></Field>
                  </div>
                  <div className="form__group">
                    <label className="form__label">Phone number *</label>
                    <div className="flex gap-2 items-start">
                      <div className="w-[170px] shrink-0">
                        <Controller name="phoneCountryCode" control={control} render={({ field }) => (
                          <SearchableSelect options={phonePrefixOptions} value={field.value} onChange={field.onChange} placeholder="Prefix" isClearable={false} />
                        )} />
                      </div>
                      <div className="flex-1">
                        <input className="form__input" placeholder="Last 10 digits" {...register("phoneNumber")} />
                      </div>
                    </div>
                    {(errors.phoneCountryCode?.message || errors.phoneNumber?.message) && (
                      <ErrorViewer message={errors.phoneNumber?.message || errors.phoneCountryCode?.message} />
                    )}
                  </div>
                  <div className="form__row">
                    <Field label="State of origin *" error={errors.originStateCode?.message}>
                      <Controller name="originStateCode" control={control} render={({ field }) => (
                        <SearchableSelect
                          options={stateOptions}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            setValue("lgaOfOriginCodeId", undefined as any, { shouldValidate: true });
                          }}
                          placeholder="Search state of origin"
                        />
                      )} />
                    </Field>
                    <Field label="LGA of origin *" error={errors.lgaOfOriginCodeId?.message}>
                      <Controller name="lgaOfOriginCodeId" control={control} render={({ field }) => (
                        <SearchableSelect
                          options={originLgaOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={originStateCode ? "Search LGA of origin" : "Select a state first"}
                          isDisabled={!originStateCode}
                        />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Residence country *" error={errors.residenceCountryCode?.message}>
                      <Controller name="residenceCountryCode" control={control} render={({ field }) => (
                        <SearchableSelect options={countryCodeOptions} value={field.value} onChange={field.onChange} placeholder="Search country" />
                      )} />
                    </Field>
                    <Field label="Residence state *" error={errors.residenceStateCode?.message}>
                      <Controller name="residenceStateCode" control={control} render={({ field }) => (
                        <SearchableSelect
                          options={stateOptions}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            setValue("residenceLocalGovernmentId", undefined as any, { shouldValidate: true });
                          }}
                          placeholder="Search state"
                        />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Residence LGA *" error={errors.residenceLocalGovernmentId?.message}>
                      <Controller name="residenceLocalGovernmentId" control={control} render={({ field }) => (
                        <SearchableSelect
                          options={lgaOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={residenceStateCode ? "Search LGA" : "Select a state first"}
                          isDisabled={!residenceStateCode}
                        />
                      )} />
                    </Field>
                    <Field label="Town / City *" error={errors.residenceTownCity?.message}><input className="form__input" {...register("residenceTownCity")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Street *" error={errors.residenceStreetName?.message}><input className="form__input" {...register("residenceStreetName")} /></Field>
                    <Field label="House number *" error={errors.residenceHouseNameOrNumber?.message}><input className="form__input" {...register("residenceHouseNameOrNumber")} /></Field>
                  </div>
                </section>
              )}
              {tab === 1 && (
                <section>
                  <div className="form__group mb-4">
                    <label className="form__label">Employer *</label>
                    <EmployerSelect onChange={onEmployerSelected} defaultValue={employerDefault} />
                  </div>
                  <Field label="Employer branch *" error={errors.employerBranchId?.message}>
                    <Controller name="employerBranchId" control={control} render={({ field }) => (
                      <SearchableSelect options={branchOptions} value={field.value} onChange={field.onChange} placeholder="Search branch" />
                    )} />
                    {(branchLoading || !employerBranchId) && <p className="text-xs text-slate-500 mt-1">Search and select an employer to load branches.</p>}
                  </Field>
                  <h3 className="text-base font-semibold mt-6 mb-3">Employment details</h3>
                  <div className="form__row">
                    <Field label="Office phone"><input className="form__input" placeholder="Office Phone Number" {...register("officePhone")} /></Field>
                    <Field label="Office email" error={errors.officeEmail?.message}><input className="form__input" type="email" placeholder="Office Email" {...register("officeEmail")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Job designation"><input className="form__input" placeholder="Job Designation" {...register("jobDesignation")} /></Field>
                    <Field label="Grade"><input className="form__input" placeholder="Grade" {...register("grade")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Employee number"><input className="form__input" placeholder="Employee NO" {...register("employeeNumber")} /></Field>
                    <Field label="Nature of business"><input className="form__input" placeholder="Nature of Business" {...register("natureOfBusiness")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Date of first appointment"><input type="date" className="form__input" {...register("dateOfFirstAppointment")} /></Field>
                    <Field label="Date of current employment"><input type="date" className="form__input" {...register("dateOfCurrentEmployment")} /></Field>
                  </div>
                </section>
              )}
              {tab === 2 && (
                <section>
                  <div className="form__row">
                    <Field label="Monthly emolument"><input type="number" className="form__input" {...register("monthlyEmolument", { valueAsNumber: true })} /></Field>
                    <Field label="Annual salary"><input type="number" className="form__input" {...register("annualSalary", { valueAsNumber: true })} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Expected employee contribution"><input type="number" className="form__input" {...register("expectedEmployeeContribution", { valueAsNumber: true })} /></Field>
                    <Field label="Expected employer contribution"><input type="number" className="form__input" {...register("expectedEmployerContribution", { valueAsNumber: true })} /></Field>
                  </div>
                  <Field label="AVC"><input type="number" className="form__input" {...register("avc", { valueAsNumber: true })} /></Field>
                </section>
              )}
              {tab === 3 && (
                <section>
                  <div className="form__row">
                    <Field label="Title *" error={errors.nokTitle?.message}>
                      <Controller name="nokTitle" control={control} render={({ field }) => (
                        <SearchableSelect options={TITLE_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search title" />
                      )} />
                    </Field>
                    <Field label="Gender *" error={errors.nokGender?.message}>
                      <Controller name="nokGender" control={control} render={({ field }) => (
                        <SearchableSelect options={NOK_GENDER_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Search gender" />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Surname *" error={errors.nokSurname?.message}><input className="form__input" {...register("nokSurname")} /></Field>
                    <Field label="First name *" error={errors.nokFirstName?.message}><input className="form__input" {...register("nokFirstName")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Middle name"><input className="form__input" {...register("nokMiddleName")} /></Field>
                    <Field label="Relationship *" error={errors.relationshipId?.message}>
                      <Controller name="relationshipId" control={control} render={({ field }) => (
                        <SearchableSelect options={relationshipOptions} value={field.value} onChange={field.onChange} placeholder="Search relationship" />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Phone *" error={errors.nokPhoneNumber?.message}><input className="form__input" {...register("nokPhoneNumber")} /></Field>
                    <Field label="Email"><input className="form__input" {...register("nokEmail")} /></Field>
                  </div>
                  <div className="form__row">
                    <Field label="Country *" error={errors.nokResidenceCountryId?.message}>
                      <Controller name="nokResidenceCountryId" control={control} render={({ field }) => (
                        <SearchableSelect options={countryIdOptions} value={field.value} onChange={field.onChange} placeholder="Search country" />
                      )} />
                    </Field>
                    <Field label="State">
                      <Controller name="nokResidenceStateCode" control={control} render={({ field }) => (
                        <SearchableSelect options={stateOptions} value={field.value} onChange={field.onChange} placeholder="Search state" />
                      )} />
                    </Field>
                  </div>
                  <div className="form__row">
                    <Field label="Town / City"><input className="form__input" {...register("nokResidenceTownCity")} /></Field>
                    <Field label="Street"><input className="form__input" {...register("nokResidenceStreetName")} /></Field>
                  </div>
                  <Field label="House number"><input className="form__input" {...register("nokResidenceHouseNumber")} /></Field>
                </section>
              )}
              {tab === 4 && (
                <section>
                  <div className="form__row">
                    <Field label="Passport photograph (jpg/png) *">
                      <input type="file" accept="image/jpeg,image/png,image/jpg" className="form__input" onChange={(e) => onPictureSelected(e.target.files?.[0] || null)} />
                      {photoSrc && (
                        <div className="mt-3 rounded-xl border bg-white p-3">
                          <p className="text-xs text-slate-500 mb-2">{pictureName || "Uploaded passport"}</p>
                          <img src={photoSrc} alt="Passport" className="h-28 w-28 object-cover rounded-xl border" />
                        </div>
                      )}
                      {faceValidating && <p className="text-xs text-slate-500 mt-2">Checking passport with Aware face validation…</p>}
                      {!faceValidating && faceStatusMessage && (
                        <div className={`mt-2 text-sm rounded-xl px-3 py-2 border ${faceCompliant ? "bg-green-50 border-green-100 text-green-800" : "bg-red-50 border-red-100 text-red-800"}`}>
                          {faceStatusMessage}
                        </div>
                      )}
                    </Field>
                    <Field label="Signature (jpg/png) *">
                      <input type="file" accept="image/jpeg,image/png,image/jpg" className="form__input" onChange={(e) => onSignatureSelected(e.target.files?.[0] || null)} />
                      {signSrc && (
                        <div className="mt-3 rounded-xl border bg-white p-3">
                          <p className="text-xs text-slate-500 mb-2">{signatureName || "Uploaded signature"}</p>
                          <img src={signSrc} alt="Signature" className="h-20 max-w-full object-contain rounded-xl border bg-white p-2" />
                        </div>
                      )}
                    </Field>
                  </div>
                  <Field label="Consent form (pdf/jpg/png) *">
                    <input type="file" accept="image/jpeg,image/png,image/jpg,application/pdf" className="form__input" onChange={(e) => onConsentSelected(e.target.files?.[0] || null)} />
                    {consentSrc && (
                      <div className="mt-3 rounded-xl border bg-white p-3">
                        <p className="text-xs text-slate-500 mb-2">{consentName || "Uploaded consent form"}</p>
                        {consentIsPdf
                          ? <a href={consentSrc} target="_blank" rel="noreferrer" className="text-sm text-primary underline">Preview consent PDF</a>
                          : <img src={consentSrc} alt="Consent" className="h-28 max-w-full object-contain rounded-xl border bg-white p-2" />}
                      </div>
                    )}
                  </Field>
                  <p className="text-xs text-slate-500 mt-2">
                    Passport photograph is checked with Aware face validation. You cannot submit for approval until the face is compliant.
                  </p>
                </section>
              )}
              {tab === 5 && (
                <section>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 16, width: "100%" }}>
                    <div style={{ flex: "1 1 0%", minWidth: 0 }} className="border border-slate-200 overflow-hidden bg-white">
                      <div className="flex items-center gap-2 border-b-2 border-orange-500 px-4 py-3">
                        <span className="text-orange-600 font-semibold">RSA Details</span>
                      </div>
                      <ReviewRow label="RSACategory" value={optionLabel(CATEGORY_OPTIONS, formValues.category)} />
                      <ReviewRow label="FUND" value="" />
                      <ReviewRow label="National Identity Number (NIN)" value={formValues.nin} />
                      <ReviewRow label="SURNAME" value={formValues.surname} />
                      <ReviewRow label="FIRSTNAME" value={formValues.firstName} />
                      <ReviewRow label="OTHERNAMES" value={formValues.middleName} />
                      <ReviewRow label="PREVIOUS NAME" value={formValues.maidenOrFormerName} />
                      <ReviewRow label="TITLE" value={optionLabel(TITLE_OPTIONS, formValues.title)} />
                      <ReviewRow label="GENDER" value={optionLabel(GENDER_OPTIONS, formValues.gender)} />
                      <ReviewRow label="Request Type" value={optionLabel(REQUEST_TYPE_OPTIONS, formValues.requestType)} />
                      <ReviewRow label="User Type" value={optionLabel(USER_TYPE_OPTIONS, formValues.rsaUserType)} />
                      <ReviewRow label="Date of Birth" value={formValues.dateOfBirth} />
                      <ReviewRow label="Phone Number" value={`${optionLabel(phonePrefixOptions, formValues.phoneCountryCode)} ${formValues.phoneNumber || ""}`.trim()} />
                      <ReviewRow label="Email" value={formValues.email} />
                      <ReviewRow label="State of Origin" value={optionLabel(stateOptions, formValues.originStateCode)} />
                      <ReviewRow label="LGA of Origin" value={optionLabel(originLgaOptions, formValues.lgaOfOriginCodeId)} />
                      <ReviewRow label="Residence State" value={optionLabel(stateOptions, formValues.residenceStateCode)} />
                      <ReviewRow label="Employer" value={employerDefault?.label} />
                      <ReviewRow label="Job Designation" value={formValues.jobDesignation} />
                      <ReviewRow label="NOK Surname" value={formValues.nokSurname} />
                      <ReviewRow label="NOK First Name" value={formValues.nokFirstName} />
                    </div>
                    <div style={{ width: 280, flexShrink: 0, position: "sticky", top: 16 }}>
                      {(faceCompliant === true || faceStatusMessage) && (
                        <p className={`text-sm font-semibold mb-2 ${faceCompliant ? "text-green-700" : "text-red-600"}`}>
                          {faceStatusMessage || (faceCompliant ? "Face is Compliant as Passport Passed all checks" : "")}
                        </p>
                      )}
                      <div className="border rounded-md p-3 bg-white mb-4">
                        {photoSrc
                          ? <img src={photoSrc} alt="Passport" className="w-[150px] h-[150px] object-cover mx-auto" />
                          : <div className="h-40 flex items-center justify-center text-slate-400 text-sm">No photograph</div>}
                        <p className={`mt-2 text-sm font-semibold text-center ${faceCompliant === true || (hasExistingPicture && faceCompliant !== false) ? "text-green-700" : "text-red-600"}`}>
                          FACE DETECTION : {faceCompliant === true || (hasExistingPicture && faceCompliant !== false) ? "True" : "False"}
                        </p>
                      </div>
                      <div className="border rounded-md p-3 bg-white">
                        {signSrc
                          ? <img src={signSrc} alt="Signature" className="w-[150px] h-[150px] object-contain mx-auto bg-white" />
                          : <div className="h-28 flex items-center justify-center text-slate-400 text-sm">No signature</div>}
                      </div>
                    </div>
                  </div>
                  <label className="mt-6 flex items-start gap-3 text-sm text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={hasAcknowledged}
                      onChange={(e) => {
                        setHasAcknowledged(e.target.checked);
                        setValue("acknowledged", e.target.checked, { shouldValidate: true });
                      }}
                    />
                    <span>
                      I confirm that all the information provided above is accurate and complete. I understand that providing false information may result in disqualification or legal consequences.
                    </span>
                  </label>
                  {!hasAcknowledged && <ErrorViewer message="Tick the box above to enable Submit for approval." />}
                </section>
              )}
            </div>
            <div className="flex flex-wrap justify-between gap-3 pt-2">
              <button type="button" className="btn btn-outline" disabled={tab === 0} onClick={() => setTab((t) => Math.max(0, t - 1))}>Previous</button>
              <div className="flex flex-wrap gap-3">
                <button type="button" className="btn btn-outline" disabled={savingDraft || submitting || !canEdit} onClick={onSaveDraft}>{savingDraft ? "Saving…" : "Save as draft"}</button>
                {tab < TABS.length - 1 ? (
                  <button type="button" className="btn btn-primary" onClick={goNext}>Next</button>
                ) : (
                  <button
                    type="button"
                    className={`btn btn-primary ${hasAcknowledged && !faceBlocksSubmit ? "" : "disabled"}`}
                    disabled={submitting || savingDraft || !canEdit || faceBlocksSubmit || !hasAcknowledged}
                    onClick={() => {
                      if (!hasAcknowledged) {
                        toast.error("Please tick the confirmation box before submitting.");
                        return;
                      }
                      if (faceBlocksSubmit) {
                        toast.error(faceStatusMessage || "Passport must pass face validation before submit.");
                        setTab(4);
                        return;
                      }
                      handleSubmit(onSubmit)();
                    }}
                  >
                    {submitting ? "Submitting…" : "Submit for approval"}
                  </button>
                )}
              </div>
            </div>
          </form>
          </>
        )}
      </Card>
    </div>
  );
};

export default InactivityTimeoutHOC(CompleteRegistrationPage, 900000);
